/**
 * Browser Sentry, loaded on demand.
 *
 * PERF CONTRACT: the Sentry browser SDK is ~150 KB of JS (measured 2026-10-12,
 * Turbopack analyze) and used to ship in every route's initial bundle. Nothing
 * on the client may import '@sentry/nextjs' statically; go through this module
 * so the SDK lands in an async chunk. instrumentation-client.ts loads it on
 * first interaction (runOnFirstInteractionOrTimeout); error boundaries load it
 * on demand when they need to report.
 */

type SentryModule = typeof import('@/lib/sentry-client-sdk');

let sentryPromise: Promise<SentryModule | null> | null = null;

/** Imports and initializes Sentry once. Resolves null when Sentry is disabled. */
export function loadSentry(): Promise<SentryModule | null> {
  if (sentryPromise) return sentryPromise;

  if (process.env.NODE_ENV !== 'production' || !process.env.NEXT_PUBLIC_SENTRY_DSN) {
    sentryPromise = Promise.resolve(null);
    return sentryPromise;
  }

  sentryPromise = import('@/lib/sentry-client-sdk')
    .then((Sentry) => {
      Sentry.init({
        dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
        environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ?? process.env.NODE_ENV,
        sendDefaultPii: true,
        tracesSampleRate: 0.1,
        enableLogs: false,
        ignoreErrors: [/WebViewJavascriptBridge/],
      });
      return Sentry;
    })
    .catch(() => null);

  return sentryPromise;
}

/** Report an error, loading Sentry first if it has not loaded yet. */
export function captureExceptionLazy(error: unknown): void {
  void loadSentry().then((Sentry) => {
    Sentry?.captureException(error);
  });
}
